// Filename: src/screens/books/BookTable.jsx

/**
 * BookTable Component
 * Enhanced data table with improved layout and styling.
 * Features:
 * - Optimized column widths 
 * - Centered 75x75 thumbnails
 * - Proper text wrapping
 * - Consistent font sizes
 *
 * @version 1.2.1
 */

import { useRef, useState } from 'react';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Button } from 'primereact/button';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputText } from 'primereact/inputtext';
import styled from 'styled-components';

const TableWrapper = styled.div`
    width: 100%;
    border: 1px solid ${props => props.theme.border.gray};
    border-radius: ${props => props.theme.radius.sizes.standard};
    overflow: hidden;
    height: calc(100vh - 180px);
    position: relative;
    display: flex;
    flex-direction: column;
`;

const StyledDataTable = styled(DataTable)`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;

    .p-datatable-wrapper {
        overflow: auto;
        height: 100%;
        flex: 1;
        padding-bottom: 8px; // Prevent last row cutoff
    }

    // Header styling
    .p-datatable-thead > tr > th {
        background: #d14141 !important;
        color: ${props => props.theme.colors.white} !important;
        padding: 0.75rem 1rem !important;
        height: 40px !important;
        border: none !important;
        font-weight: 500;
        font-size: 0.8125rem;
        position: sticky;
        top: 0;
        z-index: 2;

        &.p-sortable-column {
            .p-sortable-column-icon {
                color: rgba(255, 255, 255, 0.9) !important;
                font-size: 0.75rem;
                margin-left: 0.5rem;
            }

            &:hover {
                background: #e15151 !important;
            }

            &.p-highlight {
                background: #bc3232 !important;
            }
        }

        .p-column-header-content {
            display: flex;
            align-items: center;
            justify-content: space-between;
            min-height: 24px;
        }

        .p-column-title {
            font-weight: 500;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }
    }

    // Cell styling with proper thumbnail handling
    .p-datatable-tbody > tr > td {
        padding: 5px 1rem !important;
        height: 85px !important;
        border-bottom: 1px solid ${props => props.theme.border.divider} !important;
        font-size: 0.8125rem;
        color: ${props => props.theme.colors.font_dark};
        line-height: 1.4;

        // Thumbnail cell specific styling
        &[data-thumbnail="true"] {
            padding: 5px !important;
            width: 77px !important;
            min-width: 77px !important;
            max-width: 77px !important;

            .thumbnail-wrapper {
                width: 75px;
                height: 75px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 4px;
                background: #f5f5f5;
                overflow: hidden;

                img {
                    width: 75px;
                    height: 75px;
                    object-fit: contain;
                }
            }
        }

        // Title and Publisher cells
        &[data-wrap="true"] {
            white-space: pre-wrap;
            word-wrap: break-word;
            min-width: 200px;

            .cell-content {
                max-height: 75px;
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                line-height: 1.4;
                white-space: normal;
            }
        }

        // Regular cell content
        &[role="cell"] {
            vertical-align: middle;

            .cell-content {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            }

            .hyperlink {
                color: #bc3232;
                cursor: pointer;
                text-decoration: none;
                
                &:hover {
                    text-decoration: underline;
                }
            }
        }
    }

    // Striped rows
    .p-datatable-tbody > tr:nth-child(even) {
        background-color: ${props => props.theme.colors.background};
    }

    // Scrollable content
    .p-datatable-scrollable-header {
        overflow: hidden !important;
        position: sticky;
        top: 0;
        z-index: 2;
    }

    .p-datatable-scrollable-body {
        overflow: auto;
        
        &::-webkit-scrollbar {
            width: 8px;
            height: 8px;
        }

        &::-webkit-scrollbar-thumb {
            background-color: rgba(0, 0, 0, 0.2);
            border-radius: 4px;

            &:hover {
                background-color: rgba(0, 0, 0, 0.3);
            }
        }

        &::-webkit-scrollbar-track {
            background-color: rgba(0, 0, 0, 0.05);
        }
    }

    // Loading state
    .p-datatable-loading-overlay {
        background: rgba(255, 255, 255, 0.8);
        backdrop-filter: blur(2px);

        .p-datatable-loading-icon {
            color: #bc3232;
            font-size: 1.5rem;
        }
    }

    // Empty message
    .p-datatable-emptymessage td {
        text-align: center;
        color: ${props => props.theme.colors.icon_gray};
        font-style: italic;
        padding: 2rem !important;
        height: auto !important;
    }
`;

const FilterPopup = styled(OverlayPanel)`
    .p-overlaypanel-content {
        padding: 0.5rem; // Reduced padding
    }

    .p-inputtext {
        height: 28px; // Reduced height
        width: 100%;
        font-size: 0.8125rem;
        padding: 0.25rem 0.5rem;
        margin-bottom: 0.5rem;
    }

    .filter-actions {
        display: flex;
        justify-content: flex-end;
        gap: 0.5rem;

        .p-button {
            height: 24px; // Reduced height
            font-size: 0.8125rem;
            padding: 0.25rem 0.5rem;

            &.p-button-text {
                padding: 0.25rem 0.5rem;
                color: ${props => props.theme.colors.font_dark};

                &:hover {
                    background: rgba(0, 0, 0, 0.05);
                }
            }

            &:not(.p-button-text) {
                background: #bc3232;
                border-color: #bc3232;

                &:hover {
                    background: #d14141;
                    border-color: #d14141;
                }
            }
        }
    }
`;

const FilterButton = styled(Button)`
    width: 24px !important;
    height: 24px !important;
    padding: 0 !important;
    background: transparent !important;
    border: none !important;
    color: rgba(255, 255, 255, 0.9) !important;

    &:hover {
        background: rgba(255, 255, 255, 0.1) !important;
    }

    &.p-button-active {
        background: rgba(255, 255, 255, 0.2) !important;
    }

    .pi {
        font-size: 0.75rem !important;
    }
`;

// Custom cell templates
const thumbnailTemplate = (rowData) => {
    return (
        <div className="thumbnail-wrapper">
            <img 
                src={rowData.thumbnail} 
                alt={rowData.title}
                onError={(e) => {
                    e.target.src = '/assets/images/placeholder-book.png';
                }}
            />
        </div>
    );
};

const titleTemplate = (rowData) => {
    return (
        <div className="cell-content">
            <span className="hyperlink">{rowData.title}</span>
        </div>
    );
};

const publisherTemplate = (rowData) => {
    return (
        <div className="cell-content">
            {rowData.publishedBy}
        </div>
    );
};

const BookTable = ({
    data = [],
    columns = [],
    filters,
    setFilters,
    loading,
    onSort,
    sortField,
    sortOrder
}) => {
    const filterOverlays = useRef({});
    const [activeFilters, setActiveFilters] = useState({});
    const [tempFilters, setTempFilters] = useState({});

    // Filter handlers
    const handleFilterClick = (event, field) => {
        filterOverlays.current[field].toggle(event);
        setTempFilters(prevFilters => ({
            ...prevFilters,
            [field]: filters[field]?.value || ''
        }));
    };

    const applyFilter = (field) => {
        const newFilters = { ...filters };
        if (tempFilters[field]) {
            newFilters[field] = {
                value: tempFilters[field],
                matchMode: 'contains'
            };
        } else {
            delete newFilters[field];
        }
        setFilters(newFilters);
        setActiveFilters(prev => ({
            ...prev,
            [field]: !!tempFilters[field]
        }));
        filterOverlays.current[field].hide();
    };

    const clearFilter = (field) => {
        const newFilters = { ...filters };
        delete newFilters[field];
        setFilters(newFilters);
        setTempFilters(prev => ({
            ...prev,
            [field]: ''
        }));
        setActiveFilters(prev => ({
            ...prev,
            [field]: false
        }));
        filterOverlays.current[field].hide();
    };

    // Custom header with filter
    const renderHeader = (column) => {
        if (column.filter === false) return column.header;

        return (
            <div className="p-column-header-content">
                <span className="p-column-title">{column.header}</span>
                <FilterButton
                    type="button"
                    icon="pi pi-filter"
                    className={activeFilters[column.field] ? 'p-button-active' : ''}
                    onClick={(e) => handleFilterClick(e, column.field)}
                    aria-label={`Filter ${column.header}`}
                />
                <FilterPopup 
                    ref={(el) => filterOverlays.current[column.field] = el}
                    showCloseIcon={false}
                >
                    <InputText
                        value={tempFilters[column.field] || ''}
                        onChange={(e) => setTempFilters(prev => ({
                            ...prev,
                            [column.field]: e.target.value
                        }))}
                        placeholder={`Filter ${column.header.toLowerCase()}`}
                    />
                    <div className="filter-actions">
                        <Button
                            type="button"
                            label="Clear"
                            className="p-button-text"
                            onClick={() => clearFilter(column.field)}
                        />
                        <Button
                            type="button"
                            label="Apply"
                            onClick={() => applyFilter(column.field)}
                        />
                    </div>
                </FilterPopup>
            </div>
        );
    };

    return (
        <TableWrapper>
            <StyledDataTable
                value={data}
                dataKey="id"
                responsiveLayout="scroll"
                filters={filters}
                filterDisplay="menu"
                loading={loading}
                loadingIcon="pi pi-spinner"
                emptyMessage="No records found"
                showGridlines
                stripedRows
                removableSort
                scrollable
                scrollHeight="flex"
                resizableColumns
                columnResizeMode="fit"
                sortField={sortField}
                sortOrder={sortOrder}
                onSort={onSort}
            >
                {columns.map((col) => (
                    <Column
                        key={col.id}
                        field={col.field}
                        header={renderHeader(col)}
                        body={col.field === 'thumbnail' ? thumbnailTemplate :
                              col.field === 'title' ? titleTemplate :
                              col.field === 'publishedBy' ? publisherTemplate :
                              col.body}
                        sortable={col.sortable !== false}
                        style={{ 
                            width: col.width,
                            minWidth: col.minWidth || col.width
                        }}
                        {...(col.field === 'thumbnail' && { 'data-thumbnail': 'true' })}
                        {...(['title', 'publishedBy'].includes(col.field) && { 'data-wrap': 'true' })}
                    />
                ))}
            </StyledDataTable>
        </TableWrapper>
    );
};

export default BookTable;